import { Link } from "react-router-dom";
import {
  Scale,
  GraduationCap,
  ShieldCheck,
  Briefcase,
  AlertCircle,
  ArrowRight,
  Lock,
  FileText,
  RotateCcw,
  Users2,
} from "lucide-react";
import { PolicyPageShell } from "@/components/PolicyHeader";

const HUB = [
  {
    to: "/privacy",
    icon: Lock,
    eyebrow: "Data Protection",
    title: "Privacy Policy",
    blurb:
      "How we collect, use, share and protect your personal data — KYC, financial records, device data, your rights and our retention timelines.",
    testId: "hub-link-privacy",
  },
  {
    to: "/terms",
    icon: FileText,
    eyebrow: "Loan Agreement",
    title: "Terms & Conditions (MITC)",
    blurb:
      "Standard Terms governing loan disbursement, repayment, interest, prepayment, defaults, GST, grievance redressal mechanism, arbitration and jurisdiction.",
    testId: "hub-link-terms",
  },
  {
    to: "/refund",
    icon: RotateCcw,
    eyebrow: "Customer Charter",
    title: "Cancellation & Refund",
    blurb:
      "Rules for cancelling a loan application, what is refundable, prepayment & foreclosure, refund of excess payments and rejection terms.",
    testId: "hub-link-refund",
  },
  {
    to: "/grievance",
    icon: AlertCircle,
    eyebrow: "Customer Charter",
    title: "Grievance Redressal",
    blurb:
      "Raise a complaint with our 7-tier escalation system and SLA-backed resolution timelines (ack 24h · resolution 7 days · closure 15 days).",
    testId: "hub-link-grievance",
  },
  {
    to: "/training",
    icon: GraduationCap,
    eyebrow: "Learning & Development",
    title: "Training Programs",
    blurb:
      "Structured certification tracks for Advisors (7 modules) and DSAs (8 modules) — product knowledge, compliance, ethics + form templates.",
    testId: "hub-link-training",
  },
  {
    to: "/governance",
    icon: ShieldCheck,
    eyebrow: "Corporate Charter",
    title: "Governance & Compliance",
    blurb:
      "Vision · Mission · 8 Core Values · 9-tier organizational hierarchy · authority matrix · governance committees.",
    testId: "hub-link-governance",
  },
  {
    to: "/leadership",
    icon: Users2,
    eyebrow: "Our Team",
    title: "Leadership",
    blurb:
      "Meet Digantkumar I. Gamit — Managing Director & Founder — and the principles, focus areas and conviction behind Sahyog Finance.",
    testId: "hub-link-leadership",
  },
  {
    to: "/careers",
    icon: Briefcase,
    eyebrow: "Build Your Career",
    title: "Careers & Compensation",
    blurb:
      "Employment categories A–H · salary grades G-1 to G-7 · KPI-based appraisal · transparent promotion ladder.",
    testId: "hub-link-careers",
  },
];

export default function PolicyHub() {
  return (
    <PolicyPageShell
      testId="policy-hub-page"
      eyebrow="Policies & Governance"
      title={
        <>
          Our <em className="not-italic text-[#D4AF37]">Charters</em>,{" "}
          <span className="font-script italic text-[#F0D788]">Policies</span> &amp; Promises
        </>
      }
      lead="Transparent corporate handbook — read how we govern ourselves, train our network, redress complaints, and reward performance."
    >
      <div className="grid md:grid-cols-2 gap-6">
        {HUB.map(({ to, icon: I, eyebrow, title, blurb, testId }) => (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className="group relative rounded-3xl p-7 md:p-9 border border-[#D4AF37]/25 bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:border-[#D4AF37]/55 transition-all hover:-translate-y-1"
          >
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#F0D788] shrink-0">
                <I size={26} strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
                  {eyebrow}
                </div>
                <h2 className="mt-2 font-display text-2xl text-white group-hover:text-[#F0D788] transition-colors">
                  {title}
                </h2>
                <p className="mt-3 text-white/65 text-sm leading-relaxed">{blurb}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-[#F0D788] text-xs uppercase tracking-[0.2em]">
                  Open <ArrowRight size={13} />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-16 rounded-3xl border border-white/10 bg-white/[0.03] p-7 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
        <div className="flex items-start gap-4">
          <Scale size={28} className="text-[#D4AF37] shrink-0" />
          <div>
            <h3 className="font-display text-xl text-white">
              Final Master Corporate Handbook 2026
            </h3>
            <p className="mt-1 text-white/60 text-sm leading-relaxed">
              All policies on this site are derived from our internal Corporate Handbook —
              maintained by the Compliance Officer and reviewed annually by the Board.
            </p>
          </div>
        </div>
        <Link
          to="/"
          data-testid="hub-back-home"
          className="px-6 py-3 rounded-full btn-outline-gold text-sm font-semibold whitespace-nowrap"
        >
          Back to Home
        </Link>
      </div>
    </PolicyPageShell>
  );
}
